/** «Слова»: daily FSRS review with the method's caps (20 new zh / 10 new en, pause > 250 due). */
import { useEffect, useMemo, useState } from 'react';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { useCourseCards, useToday } from '../state/selectors.ts';
import { CardState, RATINGS, Rating, formatInterval, previewAll } from '../core/fsrs.ts';
import { NEW_CARDS_PAUSE_THRESHOLD, buildReviewQueue } from '../core/review-queue.ts';
import type { Card } from '../data/api.ts';
import { RoboCat } from '../ui/RoboCat.tsx';
import { languageInfo } from '../ui/course.ts';
import { canSpeak, speak } from '../ui/speech.ts';
import { Icon } from '../ui/Icon.tsx';
import './words.css';

const RATING_LABEL: Record<Rating, string> = {
  [Rating.Again]: 'Снова',
  [Rating.Hard]: 'Трудно',
  [Rating.Good]: 'Хорошо',
  [Rating.Easy]: 'Легко',
};

/** Cards that come back within this window are shown again in the same session. */
const SAME_SESSION_MS = 20 * 60_000;

function Front({ card }: { card: Card }) {
  const lang = languageInfo(card.lang).speechLang;
  return (
    <div className="review__front" lang={lang}>
      <p className="review__word han">{card.front.text}</p>
      {card.front.sentence && card.front.sentence !== card.front.text && (
        <p className="review__sentence han">{card.front.sentence}</p>
      )}
      {canSpeak() && (
        <button type="button" className="icon-btn" onClick={() => speak(card.front.text, lang)} aria-label="Произнести">
          <Icon name="speaker" />
        </button>
      )}
    </div>
  );
}

function Back({ card }: { card: Card }) {
  return (
    <div className="review__back">
      {card.front.pinyin && <p className="t-meta">{card.front.pinyin}</p>}
      <p className="review__ru">
        {card.back.ru}
        {card.back.pos && <span className="t-meta"> · {card.back.pos}</span>}
      </p>
      {card.back.sentenceRu && <p className="t-meta">{card.back.sentenceRu}</p>}
    </div>
  );
}

export function WordsScreen() {
  const { newSeenToday, newSeenDay } = useLoadedData();
  // Only the active course's language: Chinese words never show up in the English tab.
  const cards = useCourseCards();
  const today = useToday();
  const { reviewCard, checkAchievements } = useAppData();
  const [revealed, setRevealed] = useState(false);
  const [reviewed, setReviewed] = useState(0);
  const [catState, setCatState] = useState<'idle' | 'happy'>('idle');

  // Rebuilt from the cards after every rating; (re)learning cards due within the session
  // window are shown again right away instead of waiting for their exact minute.
  const queue = useMemo(() => {
    const at = new Date();
    const base = buildReviewQueue(cards, at, newSeenDay === today ? newSeenToday : {});
    const listed = new Set(base.cards.map((c) => c.id));
    const ahead = cards
      .filter(
        (c) =>
          !c.suspended &&
          !listed.has(c.id) &&
          (c.state === CardState.Learning || c.state === CardState.Relearning) &&
          c.due.getTime() - at.getTime() < SAME_SESSION_MS,
      )
      .sort((a, b) => a.due.getTime() - b.due.getTime());
    return { ...base, cards: [...base.cards, ...ahead] };
  }, [cards, newSeenToday, newSeenDay, today]);
  const current = queue.cards[0];
  const sessionOver = !current && reviewed > 0;
  // A finished review session may complete a «words» badge.
  useEffect(() => {
    if (sessionOver) checkAchievements();
  }, [sessionOver, checkAchievements]);
  const now = new Date();
  const preview = current && revealed ? previewAll(current, now) : null;

  const rate = (rating: Rating) => {
    if (!current) return;
    reviewCard(current, rating);
    setRevealed(false);
    setReviewed((n) => n + 1);
    if (rating !== Rating.Again) {
      setCatState('happy');
      window.setTimeout(() => setCatState('idle'), 900);
    }
  };

  const totalCards = cards.length;

  return (
    <div className="screen words">
      <div className="screen__head">
        <h1 className="t-screen">Слова</h1>
        <span className="spacer" />
        <RoboCat size={46} state={catState} alive />
      </div>

      <div className="words__stats">
        <div>
          <span className="t-number">{queue.cards.length}</span>
          <span className="t-meta">на сегодня</span>
        </div>
        <div>
          <span className="t-number">{queue.dueCount}</span>
          <span className="t-meta">повторений</span>
        </div>
        <div>
          <span className="t-number">{queue.newCount}</span>
          <span className="t-meta">новых</span>
        </div>
        <div>
          <span className="t-number">{totalCards}</span>
          <span className="t-meta">всего карточек</span>
        </div>
      </div>
      {queue.newPaused && (
        <p className="form-error">
          Повторений больше {NEW_CARDS_PAUSE_THRESHOLD} — новые слова на паузе, пока не разберём долги.
        </p>
      )}

      {current ? (
        <section className="card card--raised review" aria-label="Карточка">
          <Front card={current} />
          {revealed ? (
            <>
              <Back card={current} />
              <div className="review__ratings" role="group" aria-label="Насколько легко вспомнили?">
                {RATINGS.map((rating) => (
                  <button key={rating} type="button" className={`rating rating--${rating}`} onClick={() => rate(rating)}>
                    <span className="rating__label">{RATING_LABEL[rating]}</span>
                    {preview && <span className="t-meta">{formatInterval(now, preview[rating].due)}</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <button type="button" className="btn btn--ink btn--block" onClick={() => setRevealed(true)}>
              Показать ответ
            </button>
          )}
        </section>
      ) : (
        <section className="card review review--empty">
          <RoboCat size={96} state={reviewed > 0 ? 'happy' : 'idle'} />
          <h2 className="t-card">{reviewed > 0 ? 'На сегодня всё!' : 'Пока нечего повторять'}</h2>
          <p className="t-meta">
            {reviewed > 0
              ? `Повторено карточек: ${reviewed}. Возвращайтесь завтра.`
              : 'Добавляйте слова из уроков кнопкой «В мои слова» — они появятся здесь.'}
          </p>
        </section>
      )}
    </div>
  );
}
